import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Tooltip } from '@mui/material';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { useValidateLogin } from '../../../hooks/auth';
import { useCreditsEstimate, useCreditsStatus, useEndTrial } from '../../../hooks/billing';
import { setSettingsModal } from '../../../redux/reducers/dashboard';
import EASMIconTheme from '../../SvgIcons/EASMIconTheme';
import { AlertContext } from '../../SuccessOrFailedModal';
import { SIEMBA_CUSTOMER, SIEMBA_CUSTOMER_ENGINEER } from '../../../utils/roles';
import {
  CREDIT_BADGE_ACTIONS,
  CREDIT_BADGE_ACTION_SETTINGS_TAB,
  CREDIT_BADGE_KINDS,
  CREDIT_BADGE_TONES,
  CREDIT_COVERAGE_FEATURES,
  TRIAL_EASM_MAX_SUBDOMAINS,
  coverageCount,
  formatCredits,
  formatDays,
  resolveCreditBadge,
} from '../../../utils/constants/creditBadge';
import { getAvailableSettingsEntries } from '../../../utils/constants/settingsRegistry';

/** Estimates only change when a pricing rule does — no need to re-quote on every badge poll. */
const COVERAGE_STALE_MS = 10 * 60 * 1000;

const PILL_TONE_CLASSES = {
  [CREDIT_BADGE_TONES.NEUTRAL]: 'border-primary-500 bg-white',
  [CREDIT_BADGE_TONES.WARNING]: 'border-tertiary-400 bg-tertiary-50',
  [CREDIT_BADGE_TONES.DANGER]: 'border-functional-red bg-[#FDEFEF]',
};

const ICON_TONE_CLASSES = {
  [CREDIT_BADGE_TONES.WARNING]: '!text-tertiary-700',
  [CREDIT_BADGE_TONES.DANGER]: '!text-functional-red',
};

const ACTION_LABELS = {
  [CREDIT_BADGE_ACTIONS.VIEW_USAGE]: 'View usage',
  [CREDIT_BADGE_ACTIONS.START_EARLY]: 'Start Early Now',
  [CREDIT_BADGE_ACTIONS.UPDATE_PAYMENT]: 'Update payment',
};

/**
 * The Settings panes strip the company-scope headers and read the CALLER's own company (ROAR-4899
 * §4 decision 6), which is why the avatar menu only offers Settings to customer roles. Opening a
 * pane from the badge for a Siemba admin who selected a customer would show the wrong company's
 * billing — so for them the badge stays informational even when `billingAllowed` is true.
 *
 * The same rule gates "Start Early Now" (ROAR-5007): ending a trial starts a paid plan and charges
 * the card on file, and `billingAllowed` describes the caller's own company — an MSSP/Siemba user
 * must never convert a selected customer's trial from a badge click.
 */
const SETTINGS_ROLES = [SIEMBA_CUSTOMER, SIEMBA_CUSTOMER_ENGINEER];

const CreditIcon = () => <img src={'/images/CreditsIcon.svg'} alt={''} className={'h-5 w-5'} />;

const TimerIcon = () => <img src={'/images/TimerIcon.svg'} alt={''} className={'h-5 w-5'} />;

/** The big "500 credits" figure at the top of the trial/normal/out-of-credits hover cards. */
const CreditsRemaining = ({ available, danger, children }) => (
  <>
    <p className={'text-[13px] font-semibold uppercase tracking-wide text-body'}>
      Credits remaining
    </p>
    <p className={`mt-1 ${danger ? 'text-functional-red' : 'text-primary-500'}`}>
      <span className={'text-[32px] font-bold leading-none'}>
        {Number(available ?? 0).toLocaleString('en-US')}
      </span>
      <span className={'ml-1.5 text-[16px] font-semibold'}>
        {available === 1 ? 'credit' : 'credits'}
      </span>
    </p>
    {children}
  </>
);

CreditsRemaining.propTypes = {
  available: PropTypes.number,
  danger: PropTypes.bool,
  children: PropTypes.node,
};

/**
 * "You can cover: N <GenPT> tests or N EASM scans". The trial card adds the trial plan's subdomain
 * cap to the EASM line; the paid card does not (`showSubdomains`).
 */
const Coverage = ({ genptCount, easmCount, showSubdomains }) => {
  if (genptCount == null && easmCount == null) return null;
  return (
    <div className={'mt-4'}>
      <p className={'text-headings'}>You can cover:</p>
      <ul className={'mt-2 space-y-2'}>
        {genptCount != null && (
          <li className={'flex items-start gap-2.5'}>
            <img src={'/images/genpt_lion.svg'} alt={''} className={'mt-0.5 h-5 w-5 shrink-0'} />
            <span>
              <span className={'font-semibold text-headings'}>{genptCount} GenPT DAST</span>{' '}
              {genptCount === 1 ? 'test' : 'tests'}
              {easmCount != null && ' OR'}
            </span>
          </li>
        )}
        {easmCount != null && (
          <li className={'flex items-start gap-2.5'}>
            <span className={'mt-0.5 h-5 w-5 shrink-0 [&>svg]:h-5 [&>svg]:w-5'}>
              <EASMIconTheme />
            </span>
            <span>
              <span className={'font-semibold text-headings'}>
                {easmCount} EASM {easmCount === 1 ? 'scan' : 'scans'}
                {showSubdomains && ','}
              </span>
              {showSubdomains && ` ${TRIAL_EASM_MAX_SUBDOMAINS} subdomains per domain`}
            </span>
          </li>
        )}
      </ul>
      {genptCount != null && easmCount != null && (
        <p className={'mt-3 text-[12px]'}>
          *These are <span className={'font-semibold'}>separate maximums</span>, not additive -
          using credits on one lowers what&apos;s left for the other.
        </p>
      )}
    </div>
  );
};

Coverage.propTypes = {
  genptCount: PropTypes.number,
  easmCount: PropTypes.number,
  showSubdomains: PropTypes.bool,
};

/** "It resets on <b>October 14, 2026</b>." — or nothing, when the cycle has no reset date. */
const ResetsOn = ({ date, prefix = 'It resets on' }) =>
  date ? (
    <>
      {' '}
      {prefix} <span className={'font-semibold'}>{date}</span>.
    </>
  ) : null;

ResetsOn.propTypes = { date: PropTypes.string, prefix: PropTypes.string };

const HoverCard = ({ view, coverage, canOpenSettings, canStartEarly }) => {
  const billingBreakdown = canOpenSettings ? ' See a breakdown of your usage in Billing.' : '';

  switch (view.kind) {
    case CREDIT_BADGE_KINDS.TRIAL:
      return (
        <CreditsRemaining available={view.available}>
          <p className={'mt-2'}>Trial Period ends in {formatDays(view.trialDaysLeft)}</p>
          <Coverage {...coverage} showSubdomains />
        </CreditsRemaining>
      );
    case CREDIT_BADGE_KINDS.TRIAL_EXHAUSTED:
      return (
        <CreditsRemaining available={view.available} danger>
          {view.trialDaysLeft != null && (
            <p className={'mt-2'}>Trial Period ends in {formatDays(view.trialDaysLeft)}</p>
          )}
          <p className={'mt-3 leading-relaxed'}>
            Your trial credits are used up.
            <ResetsOn date={view.renewsOn} prefix={'Your plan automatically renews on'} />
            {canStartEarly && ' Start your plan now to keep going without waiting.'}
          </p>
        </CreditsRemaining>
      );
    case CREDIT_BADGE_KINDS.WARNING:
      return (
        <>
          <p className={'font-semibold text-headings'}>
            Your organization has used {view.pct}% of this month&apos;s credit limit.
          </p>
          <p className={'mt-3 leading-relaxed'}>
            {view.used != null && view.allotment != null
              ? `${Number(view.used).toLocaleString('en-US')} of ${formatCredits(
                  view.allotment
                )} used.`
              : null}
            <ResetsOn date={view.resetsOn} />
            {billingBreakdown}
          </p>
        </>
      );
    case CREDIT_BADGE_KINDS.EXHAUSTED:
      return (
        <>
          <p className={'font-semibold text-headings'}>
            Your organization has used 100% of this month&apos;s credit limit.
          </p>
          <p className={'mt-3 leading-relaxed'}>
            {view.allotment != null
              ? `All ${Number(view.allotment).toLocaleString('en-US')} credits are used.`
              : 'All credits are used.'}
            <ResetsOn date={view.resetsOn} />
            {billingBreakdown}
          </p>
        </>
      );
    case CREDIT_BADGE_KINDS.PAST_DUE:
      // Deliberately no timeline (AC5): the Figma frame's "[X days]" was dropped by the Story.
      return (
        <>
          <p className={'font-semibold text-headings'}>We were unable to charge your card.</p>
          <p className={'mt-3 leading-relaxed'}>
            Update your payment method to restore full access.
          </p>
        </>
      );
    default:
      return (
        <CreditsRemaining available={view.available}>
          {view.resetsOn && <p className={'mt-2'}>Resets on {view.resetsOn}</p>}
          <Coverage {...coverage} />
        </CreditsRemaining>
      );
  }
};

HoverCard.propTypes = {
  view: PropTypes.object.isRequired,
  coverage: PropTypes.object,
  canOpenSettings: PropTypes.bool,
  canStartEarly: PropTypes.bool,
};

/** The pill's own text, before the action link. */
const BadgeLabel = ({ view }) => {
  switch (view.kind) {
    case CREDIT_BADGE_KINDS.TRIAL:
      return (
        <>
          <TimerIcon />
          <span>{formatDays(view.trialDaysLeft)}</span>
          <span className={'mx-1 h-5 w-px bg-grey2'} aria-hidden={'true'} />
          <CreditIcon />
          <span>{formatCredits(view.available)}</span>
        </>
      );
    case CREDIT_BADGE_KINDS.NORMAL:
      return (
        <>
          {view.daysUntilRenewal != null && (
            <>
              <TimerIcon />
              <span>{formatDays(view.daysUntilRenewal)}</span>
              <span className={'mx-1 h-5 w-px bg-grey2'} aria-hidden={'true'} />
            </>
          )}
          <CreditIcon />
          <span>{formatCredits(view.available)}</span>
        </>
      );
    case CREDIT_BADGE_KINDS.WARNING:
      return <span>{view.pct}% credits used.</span>;
    case CREDIT_BADGE_KINDS.PAST_DUE:
      return <span>We couldn&apos;t process your payment.</span>;
    default:
      return <span>You are out of credits.</span>;
  }
};

BadgeLabel.propTypes = { view: PropTypes.object.isRequired };

/**
 * ROAR-5001 (P1.14) — the top-nav credits badge and its hover card (AC1-AC5, AC18).
 *
 * Renders for whichever company is in scope: the user's own, or the one an MSSP/Siemba user picked
 * in `GlobalCompanySelection` (the read keeps the scope headers — see `getCreditsStatus`). Renders
 * nothing while loading, on error (e.g. the 400 a Siemba user gets with no company selected), and
 * when the company has no balance at all — a badge that guesses is worse than no badge.
 *
 * The action links are offered only to users the backend says may act on billing
 * (`user.billingAllowed`, the same predicate `requireBillingAdmin` 403s on). Everyone else sees the
 * same state, without the link — informational only, per the Story.
 *
 * "Start Early Now" (AC14, ROAR-5007) calls `POST /billing/subscription/end-trial` (P1.12) behind a
 * "Skip the wait?" confirm. The plan's credits arrive by webhook after Stripe charges the card, so
 * the badge may keep its out-of-credits state until the next status poll.
 */
const CreditsBadge = () => {
  const dispatch = useDispatch();
  const [popup] = useContext(AlertContext);
  const { data: login } = useValidateLogin();
  const { mutateAsync: endTrial, isLoading: isEndingTrial } = useEndTrial();
  const { data: status } = useCreditsStatus();

  const view = resolveCreditBadge(status);
  const isTrial = view?.kind === CREDIT_BADGE_KINDS.TRIAL;
  // Only the trial and paid (`normal`) cards list "You can cover"; the warning/exhausted/past-due
  // cards don't, so they don't quote.
  const showsCoverage = isTrial || view?.kind === CREDIT_BADGE_KINDS.NORMAL;
  const coverageOptions = { enabled: showsCoverage, staleTime: COVERAGE_STALE_MS };
  const { data: genptEstimate } = useCreditsEstimate(
    isTrial ? CREDIT_COVERAGE_FEATURES.GENPT_DAST : CREDIT_COVERAGE_FEATURES.GENPT_API,
    undefined,
    coverageOptions
  );
  const { data: easmEstimate } = useCreditsEstimate(
    CREDIT_COVERAGE_FEATURES.EASM,
    undefined,
    coverageOptions
  );

  if (!view) return null;

  const billingAllowed = login?.user?.billingAllowed === true;
  const canOpenSettings = billingAllowed && SETTINGS_ROLES.includes(login?.user?.role);
  const canStartEarly = canOpenSettings;
  // ROAR-5007: a link into a Settings pane the plan hides (e.g. "Update payment" when `billing` is
  // `hide: true`) is dropped — otherwise it would open Settings on whichever pane happens to be first.
  // Resolved through the same registry filter the modal uses, so the two can't disagree.
  const targetTab = CREDIT_BADGE_ACTION_SETTINGS_TAB[view.action];
  const targetTabShown =
    !targetTab ||
    getAvailableSettingsEntries(undefined, login?.user?.features).some(
      (entry) => entry.key === targetTab
    );
  const actionEnabled =
    (view.action === CREDIT_BADGE_ACTIONS.START_EARLY ? canStartEarly : canOpenSettings) &&
    targetTabShown;

  const coverage = {
    genptCount: coverageCount(view.available, genptEstimate?.default_estimated_credits),
    easmCount: coverageCount(view.available, easmEstimate?.default_estimated_credits),
  };

  const onStartEarly = async () => {
    await popup({
      variant: 'warning',
      title: 'Skip the wait?',
      description:
        'Your trial will end now and your plan will start immediately. Your card on file will be charged for the plan.',
      okayText: 'Start Now',
      cancelText: 'Cancel',
    });
    try {
      await endTrial();
      popup({
        variant: 'success',
        title: 'Your plan has started',
        description: 'Your new credits will appear shortly.',
        okayText: 'Okay',
      });
    } catch (error) {
      popup({
        variant: 'error',
        title: 'Error',
        description:
          error?.response?.status === 404
            ? 'Your trial has already ended.'
            : "We couldn't start your plan. Please try again.",
        okayText: 'Okay',
      });
    }
  };

  const onAction = (event) => {
    event.stopPropagation();
    if (view.action === CREDIT_BADGE_ACTIONS.START_EARLY) {
      if (!isEndingTrial) onStartEarly();
      return;
    }
    dispatch(setSettingsModal({ open: true, tab: CREDIT_BADGE_ACTION_SETTINGS_TAB[view.action] }));
  };

  return (
    <Tooltip
      arrow
      placement={'bottom'}
      enterTouchDelay={0}
      title={
        <div className={'text-left text-[14px] text-body'}>
          <HoverCard
            view={view}
            coverage={coverage}
            canOpenSettings={canOpenSettings}
            canStartEarly={canStartEarly}
          />
        </div>
      }
      componentsProps={{
        tooltip: {
          sx: {
            backgroundColor: 'var(--white)',
            color: 'inherit',
            maxWidth: 360,
            padding: '20px 24px',
            borderRadius: '6px',
            boxShadow: '0 4px 16px var(--shadow)',
          },
        },
        arrow: { sx: { color: 'var(--white)' } },
      }}
    >
      <div
        tabIndex={0}
        data-testid={'credits-badge'}
        className={`flex items-center gap-1.5 whitespace-nowrap rounded-full border px-4 py-1.5 text-[14px] font-semibold text-headings xl:text-[15px] ${
          PILL_TONE_CLASSES[view.tone]
        }`}
      >
        {view.tone !== CREDIT_BADGE_TONES.NEUTRAL && (
          <WarningAmberOutlinedIcon fontSize={'small'} className={ICON_TONE_CLASSES[view.tone]} />
        )}
        <BadgeLabel view={view} />
        {view.action && actionEnabled && (
          <button
            type={'button'}
            onClick={onAction}
            disabled={isEndingTrial}
            className={'ml-1 text-primary-500 underline underline-offset-2'}
          >
            {ACTION_LABELS[view.action]} &rarr;
          </button>
        )}
      </div>
    </Tooltip>
  );
};

export default CreditsBadge;
